import { Injectable, signal, computed } from '@angular/core';
import {
  PublishingEvent,
  PublishingStatus,
} from '../../domain/publishing-status';
import type { PublishingDateRangeKey } from '../../domain/publishing-status';

const DEFAULT_PAGE_SIZE = 10;

/**
 * Signal-based state store for the Publishing Status module.
 * Single source of truth — all publishing status data flows through this service.
 */
@Injectable({ providedIn: 'root' })
export class PublishingStatusStateService {
  // ── Private writable signals ──
  private readonly itemsSignal = signal<PublishingEvent[]>([]);
  private readonly totalSignal = signal<number>(0);
  private readonly pageSizeSignal = signal<number>(DEFAULT_PAGE_SIZE);
  private readonly offsetSignal = signal<number>(0);
  private readonly searchQuerySignal = signal<string>('');
  private readonly statusFilterSignal = signal<PublishingStatus | 'All'>('All');
  private readonly dateRangeKeySignal = signal<PublishingDateRangeKey>('all');
  private readonly isLoadingSignal = signal<boolean>(false);
  private readonly errorSignal = signal<string | null>(null);
  private readonly selectedIdsSignal = signal<ReadonlySet<string>>(new Set());
  private readonly isExportingToMamSignal = signal<boolean>(false);
  private readonly isDeletingSignal = signal<boolean>(false);

  // ── Public readonly signals ──
  readonly items = this.itemsSignal.asReadonly();
  readonly total = this.totalSignal.asReadonly();
  readonly pageSize = this.pageSizeSignal.asReadonly();
  readonly offset = this.offsetSignal.asReadonly();
  readonly searchQuery = this.searchQuerySignal.asReadonly();
  readonly statusFilter = this.statusFilterSignal.asReadonly();
  readonly dateRangeKey = this.dateRangeKeySignal.asReadonly();
  readonly isLoading = this.isLoadingSignal.asReadonly();
  readonly error = this.errorSignal.asReadonly();
  readonly selectedIds = this.selectedIdsSignal.asReadonly();
  readonly isExportingToMam = this.isExportingToMamSignal.asReadonly();
  readonly isDeleting = this.isDeletingSignal.asReadonly();

  readonly selectedCount = computed(() => this.selectedIdsSignal().size);

  readonly isBatchBusy = computed(
    () => this.isExportingToMamSignal() || this.isDeletingSignal(),
  );

  readonly canExportToMam = computed(
    () => this.selectedIdsSignal().size > 0 && !this.isBatchBusy(),
  );

  readonly canDelete = computed(
    () => this.selectedIdsSignal().size > 0 && !this.isBatchBusy(),
  );

  /** Zero-based current page index derived from offset and page size. */
  readonly pageIndex = computed(() => {
    const size = this.pageSizeSignal();
    return size > 0 ? Math.floor(this.offsetSignal() / size) : 0;
  });

  readonly totalPages = computed(() => {
    const size = this.pageSizeSignal();
    const total = this.totalSignal();
    return size > 0 ? Math.max(1, Math.ceil(total / size)) : 1;
  });

  // ── Setters (called by container component) ──

  setPageResult(result: { items: PublishingEvent[]; total: number }): void {
    this.itemsSignal.set(result.items);
    this.totalSignal.set(result.total);
  }

  setPageSize(size: number): void {
    this.pageSizeSignal.set(size);
  }

  setOffset(offset: number): void {
    this.offsetSignal.set(offset);
  }

  setSearchQuery(query: string): void {
    this.searchQuerySignal.set(query);
  }

  setStatusFilter(status: PublishingStatus | 'All'): void {
    this.statusFilterSignal.set(status);
  }

  setDateRangeKey(key: PublishingDateRangeKey): void {
    this.dateRangeKeySignal.set(key);
  }

  setLoading(loading: boolean): void {
    this.isLoadingSignal.set(loading);
  }

  setError(error: string | null): void {
    this.errorSignal.set(error);
  }

  setExportingToMam(exporting: boolean): void {
    this.isExportingToMamSignal.set(exporting);
  }

  setDeleting(deleting: boolean): void {
    this.isDeletingSignal.set(deleting);
  }

  isSelected(publicationUid: string): boolean {
    return this.selectedIdsSignal().has(publicationUid);
  }

  toggleSelection(publicationUid: string, selected: boolean): void {
    const next = new Set(this.selectedIdsSignal());
    if (selected) {
      next.add(publicationUid);
    } else {
      next.delete(publicationUid);
    }
    this.selectedIdsSignal.set(next);
  }

  clearSelection(): void {
    this.selectedIdsSignal.set(new Set());
  }

  getSelectedPublicationUids(): string[] {
    return [...this.selectedIdsSignal()];
  }

  /** Reset all state to defaults. */
  reset(): void {
    this.itemsSignal.set([]);
    this.totalSignal.set(0);
    this.pageSizeSignal.set(DEFAULT_PAGE_SIZE);
    this.offsetSignal.set(0);
    this.searchQuerySignal.set('');
    this.statusFilterSignal.set('All');
    this.dateRangeKeySignal.set('all');
    this.isLoadingSignal.set(false);
    this.errorSignal.set(null);
    this.selectedIdsSignal.set(new Set());
    this.isExportingToMamSignal.set(false);
    this.isDeletingSignal.set(false);
  }
}
